<template>
  <v-container fluid>
    <v-btn text @click="$router.back()">
      <v-icon left>mdi-arrow-left</v-icon> {{ $t("back") }}
    </v-btn>

    <h2 class="font-weight-bold my-4">{{ $t("add_product") }}</h2>

    <v-form ref="form" v-model="valid" lazy-validation>
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="form.name"
            :label="$t('product_name')"
            :rules="[rules.required]"
            persistent-hint
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.box_barcode"
            :label="$t('box_barcode')"
            @input="handleInput"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.barcode"
            :label="$t('product_barcode')"
            :rules="[rules.required]"
            @input="handleInput"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-select
            v-model="form.unit"
            :items="unitOptions"
            :label="$t('unit')"
            :rules="[rules.required]"
            outlined
            dense
            @change="onUnitUpdate()"
          />
        </v-col>

        <v-col cols="12">
          <v-autocomplete
            v-model="form.sub_category_id"
            :items="subcategories"
            :loading="loadingSubcategories"
            :search-input.sync="subcategorySearch"
            item-text="name"
            item-value="id"
            :label="$t('subcategory')"
            :rules="[rules.required]"
            outlined
            dense
            clearable
          />
        </v-col>

        <v-col cols="12">
          <v-radio-group
            v-model="form.type"
            row
            :disabled="form.unit.toLowerCase() === 'kg'"
          >
            <v-radio :label="$t('buy_single_item')" value="item"></v-radio>
            <v-radio :label="$t('buy_box_item')" value="box"></v-radio>
            <v-radio :label="$t('buy_both_item')" value="both"></v-radio>
          </v-radio-group>
        </v-col>

        <template v-if="form.unit.toLowerCase() === 'quantity'">
          <template v-if="form.type === 'both'">
            <v-col cols="4">
              <v-text-field
                v-model="boxes"
                :label="$t('quantity_of_boxes')"
                type="number"
                min="0"
                outlined
                dense
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="single_item"
                :label="$t('quantity_of_product')"
                type="number"
                min="0"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.purchased_price"
                :label="$t('purchased_price_per_box')"
                type="number"
                min="0"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.selling_price_per_box"
                :label="$t('selling_price_per_box')"
                type="number"
                min="0"
                outlined
                dense
              />
            </v-col>
          </template>
          <v-col cols="12" v-else>
            <v-text-field
              v-model="form.total_quantity"
              :label="$t('total_quantity')"
              type="number"
              min="0"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.quantity_per_box"
              :label="$t('products_per_box')"
              type="number"
              min="0"
              outlined
              dense
            />
          </v-col>
        </template>

        <template v-else-if="form.unit.toLowerCase() === 'kg'">
          <v-col cols="12">
            <v-text-field
              v-model="form.total_quantity"
              :label="$t('total_kg')"
              type="number"
              min="0"
              outlined
              dense
            />
          </v-col>
        </template>

        <v-col
          cols="12"
          v-if="form.unit.toLowerCase() === 'quantity' && form.type === 'box'"
        >
          <v-text-field
            v-model="form.purchased_price"
            :label="$t('purchased_price_per_box')"
            type="number"
            min="0"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.purchased_price_per_product"
            :label="$t('purchased_price_per_product')"
            type="number"
            min="0"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.day_before_expiry"
            :label="$t('day_before_expiry')"
            type="number"
            min="0"
            outlined
            dense
          />
        </v-col>

        <v-col
          cols="12"
          v-if="form.unit.toLowerCase() === 'quantity' && form.type === 'box'"
        >
          <v-text-field
            v-model="form.selling_price_per_box"
            :label="$t('selling_price_per_box')"
            type="number"
            min="0"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="form.selling_price"
            :label="$t('selling_price_per_product')"
            type="number"
            min="0"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <div>
            <label class="font-weight-medium">
              {{ $t("selling_price_per_multiple_products") }}
            </label>

            <v-row dense class="mt-1">
              <v-col cols="3">
                <v-text-field
                  v-model="form.selling_price_multiple"
                  :label="$t('selling_price')"
                  type="number"
                  min="0"
                  outlined
                  dense
                />
              </v-col>

              <v-col
                cols="1"
                class="d-flex align-center justify-center font-weight-medium"
                style="margin-top: -8px"
              >
                {{ $t("per") }}
              </v-col>

              <v-col cols="3">
                <v-text-field
                  v-model="form.selling_price_multiple_count"
                  :label="$t('number_of_products')"
                  type="number"
                  min="1"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
          </div>
        </v-col>

        <v-col cols="12">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.expiry_date"
                :label="$t('expiry_date')"
                readonly
                dense
                outlined
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="form.expiry_date"
              no-title
              @input="menu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="form.stock_management"
            :label="$t('stock_notification')"
            type="number"
            min="1"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.box_wholesale_price"
            :label="$t('box_wholesale_price')"
            type="number"
            min="0"
            step="0.01"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.box_half_wholesale_price"
            :label="$t('box_half_wholesale_price')"
            type="number"
            min="0"
            step="0.01"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.item_wholesale_price"
            :label="$t('item_wholesale_price')"
            type="number"
            min="0"
            step="0.01"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.item_half_wholesale_price"
            :label="$t('item_half_wholesale_price')"
            type="number"
            min="0"
            step="0.01"
            outlined
            dense
          />
        </v-col>

        <v-col cols="12">
          <v-textarea
            v-model="form.notes"
            :label="$t('note')"
            required
            outlined
            dense
            auto-grow
          />
        </v-col>

        <v-col cols="1">
          <v-btn color="amber darken-2" :disabled="!valid" @click="submitForm">
            {{ $t("add") }}
          </v-btn>
        </v-col>
        <v-col cols="3">
          <div class="mt-2" style="color: red" v-if="form.type === 'item'">
            {{ $t("total_purchase_price") }}: {{ totalProducts }} IQD
          </div>
          <div class="mt-2" style="color: red" v-else-if="form.type === 'box'">
            {{ $t("total_purchase_price") }}: {{ totalProductsBox }} IQD
          </div>
          <div class="mt-2" style="color: red" v-else>
            {{ $t("total_purchase_price") }}: {{ totalProductsBoth }} IQD
          </div>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import api from "@/services/axios";

export default {
  data() {
    return {
      prevFilter: null,
      single_item: 0,
      boxes: 0,
      valid: false,
      menu: false,
      subcategorySearch: "",
      loadingSubcategories: false,
      subcategories: [],
      unitOptions: ["Quantity", "KG"],
      form: {
        name: "",
        barcode: "",
        unit: "Quantity",
        sub_category_id: null,
        product_id: "",
        total_quantity: "",
        quantity_per_box: "",
        purchased_price_per_product: "",
        selling_price: "",
        selling_price_per_box: "",
        purchased_price: "",
        expiry_date: "",
        box_barcode: "",
        day_before_expiry: null,
        type: "item",
        selling_price_multiple: null,
        selling_price_multiple_count: null,
        notes: "",
        stock_management: null,
        box_wholesale_price: null,
        box_half_wholesale_price: null,
        item_wholesale_price: null,
        item_half_wholesale_price: null,
      },
      rules: {
        required: (v) => !!v || this.$t("field_required"),
      },
    };
  },
  watch: {
    subcategorySearch(val) {
      clearTimeout(this._searchTimeout);
      if (val && val.length >= 1) {
        this._searchTimeout = setTimeout(() => {
          this.searchSubcategories(val);
        }, 300);
      } else if (val == "") {
        this.searchSubcategories("");
      }
    },
    "form.purchased_price"() {
      this.updatePurchasedPricePerProduct();
    },
    "form.quantity_per_box"() {
      this.updatePurchasedPricePerProduct();
    },
  },
  computed: {
    totalProducts() {
      return (
        Number(this.form.total_quantity || 0) *
        Number(this.form.purchased_price_per_product || 0)
      );
    },
    totalProductsBox() {
      return (
        Number(this.form.total_quantity || 0) *
        Number(this.form.purchased_price || 0)
      );
    },
    totalProductsBoth() {
      const total =
        Number(this.single_item || 0) +
        Number(this.boxes || 0) * Number(this.form.quantity_per_box || 0);
      return total * Number(this.form.purchased_price_per_product || 0);
    },
  },
  mounted() {
    this.searchSubcategories("");
    this.debouncedSearch = this.debounce(this.searchAction, 500);
    if (this.$route.params.id) {
      this.fetchDetails();
    }
  },
  methods: {
    debounce(func, wait) {
      let timeout;
      return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
      };
    },

    updatePurchasedPricePerProduct() {
      if (this.form.type !== "item") {
        const price = Number(this.form.purchased_price || 0);
        const quantity = Number(this.form.quantity_per_box || 0);
        if (price > 0 && quantity > 0) {
          this.form.purchased_price_per_product = (price / quantity).toFixed(2);
        }
      }
    },

    handleInput() {
      this.debouncedSearch(this.searchText);
    },

    onUnitUpdate() {
      if (this.form.unit.toLowerCase() === "kg") {
        this.form.type = "item";
      }
    },

    async searchAction() {
      try {
        const res = await api.get(
          `/product/barcode?barcode=${
            this.form.barcode || this.form.box_barcode
          }`
        );
        if (res.data.status) {
          const data = res.data.data;
          this.form.name = data.name;
          this.form.sub_category_id = data.subcategory.id;
          this.form.unit = data.unit == "kg" ? "KG" : "Quantity";
          this.form.product_id = data.id;
          this.form.barcode = data.barcode;
          if (!this.form.barcode) {
            this.form.box_barcode = data.box_barcode;
          }
          this.form.day_before_expiry = data.day_before_expiry;
          this.form.quantity_per_box = data.quantity_per_box;
          if (data.selling_price_multiple) {
            this.form.selling_price_multiple = data.selling_price_multiple;
          }
          if (data.purchase_price_per_box) {
            this.form.purchased_price = data.purchase_price_per_box;
          }
          if (data.selling_price_per_box) {
            this.form.selling_price_per_box = data.selling_price_per_box;
          }
          if (data.purchase_price_per_item) {
            this.form.purchased_price_per_product = data.purchase_price_per_item;
          }
          if (data.selling_price_per_item) {
            this.form.selling_price = data.selling_price_per_item;
          }
          if (data.selling_price_multiple_count) {
            this.form.selling_price_multiple_count =
              data.selling_price_multiple_count;
          }
          if (data.notes) {
            this.form.notes = data.notes;
          }
          if (data.stock_management) {
            this.form.stock_management = data.stock_management;
          }
          this.searchSubcategories(data.subcategory.name);
          this.subcategorySearch = data.subcategory.name;
          if (data.unit == "kg") {
            this.onUnitUpdate();
          }
        }
      } catch (err) {
        console.error("Error fetching product details:", err);
      }
    },
    async fetchDetails() {
      try {
        const res = await api.get(`/product/view/${this.$route.params.id}`);
        const data = res.data.data;
        this.form.name = data.name;
        this.form.unit = data.unit === "kg" ? "KG" : "Quantity";
        this.form.product_id = data.id;
      } catch (err) {
        console.error("Error fetching product details:", err);
      }
    },
    async searchSubcategories(filter) {
      if (this.prevFilter === filter) {
        return;
      }
      this.prevFilter = filter;
      this.loadingSubcategories = true;
      try {
        const res = await api.get("/category/sub-category/list", {
          params: { filter },
        });
        this.subcategories = res.data.data;
      } catch (err) {
        console.error("Subcategory fetch error:", err);
      } finally {
        this.loadingSubcategories = false;
      }
    },
    async submitForm() {
      if (!this.$refs.form.validate()) return;
      if (this.form.type === "both") {
        if (!this.single_item) {
          return;
        }
        if (!this.boxes) {
          return;
        }
      }
      const formData = new FormData();
      formData.append("name", this.form.name);
      formData.append("sub_category_id", this.form.sub_category_id);
      formData.append("barcode", this.form.barcode);
      formData.append("box_barcode", this.form.box_barcode);
      formData.append("unit", this.form.unit.toLowerCase());
      formData.append("product_id", this.form.product_id);
      if (this.form.type === "both") {
        formData.append(
          "total_quantity",
          Number(this.single_item) +
            Number(this.boxes * this.form.quantity_per_box)
        );
      } else {
        formData.append("total_quantity", this.form.total_quantity);
      }
      formData.append("type", this.form.type === "box" ? "box" : "item");
      if (this.form.unit.toLowerCase() === "quantity") {
        if (this.form.quantity_per_box) {
          formData.append("quantity_per_box", this.form.quantity_per_box);
        }
        if (this.form.selling_price_per_box) {
          formData.append(
            "selling_price_per_box",
            this.form.selling_price_per_box
          );
        }
        if (this.form.purchased_price) {
          formData.append("purchased_price", this.form.purchased_price);
        }
      }
      if (this.form.purchased_price_per_product) {
        formData.append(
          "purchased_price_per_product",
          this.form.purchased_price_per_product
        );
      }
      if (this.form.selling_price) {
        formData.append("selling_price", this.form.selling_price);
      }
      if (this.form.selling_price_multiple) {
        formData.append(
          "selling_price_multiple",
          this.form.selling_price_multiple
        );
      }
      if (this.form.selling_price_multiple_count) {
        formData.append(
          "selling_price_multiple_count",
          this.form.selling_price_multiple_count
        );
      }
      if (this.form.day_before_expiry) {
        formData.append("day_before_expiry", this.form.day_before_expiry);
      }
      if (this.form.expiry_date) {
        formData.append("expiry_date", this.form.expiry_date);
      }
      if (this.form.box_wholesale_price)
        formData.append("box_wholesale_price", this.form.box_wholesale_price);
      if (this.form.box_half_wholesale_price)
        formData.append(
          "box_half_wholesale_price",
          this.form.box_half_wholesale_price
        );
      if (this.form.item_wholesale_price)
        formData.append("item_wholesale_price", this.form.item_wholesale_price);
      if (this.form.item_half_wholesale_price)
        formData.append(
          "item_half_wholesale_price",
          this.form.item_half_wholesale_price
        );
      if (this.form.notes) formData.append("notes", this.form.notes);
      if (this.form.stock_management)
        formData.append("stock_management", this.form.stock_management);
      try {
        const res = await api.post("/product/add", formData);
        if (!res.data.status) {
          this.$root.$emit("show-snackbar", {
            message: this.$te(res.data.locale_key)
              ? res.data.locale_key
              : res.data.message,
            color: "error",
          });
        } else {
          this.$root.$emit("show-snackbar", {
            message: this.$te(res.data.locale_key)
              ? res.data.locale_key
              : res.data.message,
            color: "success",
          });
          this.$router.back();
        }
      } catch (err) {
        console.error("Product add failed:", err);
      }
    },
  },
};
</script>
